import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  CheckCircle, Clock, Calendar, 
  ChevronDown, ChevronUp, User, 
  RotateCcw, FileCheck, Download, Send, Building2
} from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';

const EPMSPValidation = ({ user }) => {
  const [stagiaires, setStagiaires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedStagiaire, setExpandedStagiaire] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');

  // Les 4 épreuves EPMSP
  const EPREUVES = [
    { id: 'vnm_selle', nom: "Séances de découverte VNM à selle" },
    { id: 'engin_tracte', nom: "Traction d'un engin tracté" },
    { id: 'secours', nom: "Situation de secours" },
    { id: 'initiation_choix', nom: "Séance d'initiation sur support au choix (ESH/PNM/Jet à Bras)", hasChoice: true }
  ];

  const SUPPORTS_CHOIX = ['ESH', 'PNM', 'Jet à Bras'];

  useEffect(() => {
    loadEPMSPData();
  }, []);

  const loadEPMSPData = async () => {
    try {
      const response = await axios.get('/epmsp/all');
      setStagiaires(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des données EPMSP');
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async (stagiaireId, epreuve, statut, dateEpreuve, dateRattrapage, supportChoisi, commentaire, isNewAttempt = false) => {
    try {
      const response = await axios.post('/epmsp/validate', {
        stagiaire_id: stagiaireId,
        epreuve: epreuve,
        statut: statut,
        date_epreuve: dateEpreuve,
        date_rattrapage: dateRattrapage,
        support_choisi: supportChoisi,
        commentaire: commentaire,
        is_new_attempt: isNewAttempt
      });
      
      if (response.data.all_validated) {
        toast.success('🎉 Toutes les épreuves sont validées ! Notifications envoyées au stagiaire, tuteur, formateur et entreprise.');
      } else if (response.data.action === 'rattrapage_needed') {
        toast.info('Rattrapage enregistré. Utilisez "Valider rattrapage" quand le stagiaire aura repassé l\'épreuve.');
      } else if (response.data.attempt_number) {
        toast.success(`Rattrapage validé (tentative #${response.data.attempt_number})`);
      } else {
        toast.success('Épreuve enregistrée');
      }
      
      loadEPMSPData();
    } catch (error) {
      toast.error('Erreur lors de la validation');
    }
  };

  const filteredStagiaires = stagiaires.filter(stag => {
    if (filterStatus === 'all') return true;
    if (filterStatus === 'complete') return stag.all_validated;
    if (filterStatus === 'incomplete') return !stag.all_validated;
    return true;
  });

  // Stats globales
  const totalStagiaires = stagiaires.length;
  const stagValidatedAll = stagiaires.filter(s => s.all_validated).length;
  const stagInProgress = totalStagiaires - stagValidatedAll;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#8DC63F]"></div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm" data-testid="epmsp-validation">
      {/* Header */}
      <div className="p-4 border-b border-slate-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-[#8DC63F]/10 p-2 rounded-lg">
              <FileCheck className="h-6 w-6 text-[#8DC63F]" />
            </div>
            <div>
              <h2 className="text-lg font-display font-semibold text-[#0F172A]">Épreuves EPMSP</h2>
              <p className="text-sm text-slate-500">Validation obligatoire avant le face à face pédagogique</p>
            </div>
          </div>
          <div className="flex gap-4 text-sm">
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">{stagValidatedAll}</p>
              <p className="text-xs text-slate-500">Prêts pour le stage</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-orange-500">{stagInProgress}</p>
              <p className="text-xs text-slate-500">En cours</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div className="p-4 border-b border-slate-100 bg-slate-50">
        <div className="flex gap-2">
          <button
            onClick={() => setFilterStatus('all')}
            className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
              filterStatus === 'all' 
                ? 'bg-[#8DC63F] text-white' 
                : 'bg-white text-slate-600 hover:bg-slate-100'
            }`}
          >
            Tous ({totalStagiaires})
          </button>
          <button
            onClick={() => setFilterStatus('incomplete')}
            className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
              filterStatus === 'incomplete' 
                ? 'bg-orange-500 text-white' 
                : 'bg-white text-slate-600 hover:bg-slate-100'
            }`}
          >
            En cours ({stagInProgress})
          </button>
          <button
            onClick={() => setFilterStatus('complete')}
            className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
              filterStatus === 'complete' 
                ? 'bg-green-500 text-white' 
                : 'bg-white text-slate-600 hover:bg-slate-100'
            }`}
          >
            Validés ({stagValidatedAll})
          </button>
        </div>
      </div>

      {/* Liste des stagiaires */}
      <div className="divide-y divide-slate-100 max-h-[800px] overflow-y-auto">
        {filteredStagiaires.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            Aucun stagiaire trouvé
          </div>
        ) : (
          filteredStagiaires.map((stag) => (
            <div key={stag.stagiaire_id} className="border-b border-slate-100 last:border-b-0">
              {/* Ligne stagiaire */}
              <div 
                className={`p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors ${
                  stag.all_validated ? 'bg-green-50/50' : ''
                }`}
                onClick={() => setExpandedStagiaire(expandedStagiaire === stag.stagiaire_id ? null : stag.stagiaire_id)}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    stag.all_validated ? 'bg-green-100' : 'bg-slate-100'
                  }`}>
                    <User className={`h-5 w-5 ${stag.all_validated ? 'text-green-600' : 'text-slate-500'}`} />
                  </div>
                  <div>
                    <p className="font-medium text-[#0F172A]">{stag.prenom} {stag.nom}</p>
                    <p className="text-xs text-slate-500">{stag.centre_formation}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 sm:gap-4 shrink-0">
                  {/* Progression */}
                  <div className="flex items-center gap-2">
                    <div className="w-12 sm:w-24 h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all ${
                          stag.all_validated ? 'bg-green-500' : 'bg-orange-400'
                        }`}
                        style={{ width: `${(stag.validated_count / stag.total_epreuves) * 100}%` }}
                      />
                    </div>
                    <span className="text-sm text-slate-600">{stag.validated_count}/{stag.total_epreuves}</span>
                  </div>
                  {stag.all_validated && (
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full hidden sm:flex items-center gap-1">
                      <CheckCircle className="h-3 w-3" />
                      Prêt
                    </span>
                  )}
                  {expandedStagiaire === stag.stagiaire_id ? (
                    <ChevronUp className="h-5 w-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-slate-400" />
                  )}
                </div>
              </div>

              {/* Détail des épreuves (expandable) */}
              {expandedStagiaire === stag.stagiaire_id && (
                <div className="p-4 bg-slate-50 border-t border-slate-100">
                  <div className="space-y-3">
                    {EPREUVES.map((epreuve) => {
                      const validation = stag.epreuves[epreuve.nom] || { statut: 'en_attente' };
                      return (
                        <EpreuveRow
                          key={epreuve.id}
                          epreuve={epreuve}
                          validation={validation}
                          stagiaireId={stag.stagiaire_id}
                          onValidate={handleValidate}
                          SUPPORTS_CHOIX={SUPPORTS_CHOIX}
                        />
                      );
                    })}
                  </div>
                  
                  {/* Section Certificat EPMSP - visible seulement si tout est validé */}
                  {stag.all_validated && (
                    <CertificatActions 
                      stagiaireId={stag.stagiaire_id}
                      stagiaireNom={`${stag.prenom} ${stag.nom}`}
                    />
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// Composant pour une ligne d'épreuve
const EpreuveRow = ({ epreuve, validation, stagiaireId, onValidate, SUPPORTS_CHOIX }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isRattrapageEditing, setIsRattrapageEditing] = useState(false);
  const [statut, setStatut] = useState(validation.statut || 'en_attente');
  const [dateEpreuve, setDateEpreuve] = useState(validation.date_epreuve || '');
  const [dateRattrapage, setDateRattrapage] = useState(validation.date_rattrapage || '');
  const [supportChoisi, setSupportChoisi] = useState(validation.support_choisi || '');
  const [commentaire, setCommentaire] = useState(validation.commentaire || '');

  // Pour le rattrapage (second passage)
  const [dateSecondPassage, setDateSecondPassage] = useState('');
  const [commentaireRattrapage, setCommentaireRattrapage] = useState('');

  const handleSave = () => {
    if (!dateEpreuve) {
      toast.error('Veuillez entrer la date de l\'épreuve');
      return;
    }
    if (epreuve.hasChoice && !supportChoisi && statut !== 'en_attente') {
      toast.error('Veuillez sélectionner le support choisi');
      return;
    }
    onValidate(stagiaireId, epreuve.nom, statut, dateEpreuve, dateRattrapage, supportChoisi, commentaire);
    setIsEditing(false);
  };

  const handleRattrapageSave = () => {
    if (!dateSecondPassage) {
      toast.error('Veuillez entrer la date du rattrapage');
      return;
    }
    // Valider le rattrapage avec is_new_attempt = true
    onValidate(stagiaireId, epreuve.nom, 'valide', dateSecondPassage, '', supportChoisi, commentaireRattrapage, true);
    setIsRattrapageEditing(false);
  };

  const getStatusBadge = (s) => {
    switch (s) {
      case 'valide':
        return <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium">Validé</span>;
      case 'rattrapage':
        return <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full font-medium">Rattrapage requis</span>;
      case 'rattrapage_valide':
        return <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium">Validé</span>;
      default:
        return <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">En attente</span>;
    }
  };

  // Historique des tentatives (passages précédents)
  const history = validation.history || [];
  
  // Construire la liste complète des passages pour affichage
  const allPassages = [];
  
  // Ajouter les passages de l'historique (du plus ancien au plus récent)
  history.forEach((h, idx) => {
    allPassages.push({
      numero: idx + 1,
      date: h.date_epreuve,
      statut: h.statut,
      commentaire: h.commentaire,
      support: h.support_choisi,
      isHistorique: true
    });
  });
  
  // Ajouter le passage actuel (seulement si pas en attente)
  if (validation.statut !== 'en_attente') {
    allPassages.push({
      numero: history.length + 1,
      date: validation.date_epreuve,
      statut: validation.statut,
      commentaire: validation.commentaire,
      support: validation.support_choisi,
      isHistorique: false,
      isCurrent: true
    });
  }

  return (
    <div className={`p-4 rounded-lg border ${
      validation.statut === 'valide' || validation.statut === 'rattrapage_valide'
        ? 'bg-green-50 border-green-200'
        : validation.statut === 'rattrapage'
        ? 'bg-orange-50 border-orange-200'
        : 'bg-white border-slate-200'
    }`}>
      {/* En-tête de l'épreuve */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <p className="font-semibold text-sm text-[#0F172A]">{epreuve.nom}</p>
          {validation.support_choisi && (
            <span className="inline-block mt-1 bg-purple-100 text-purple-700 px-2 py-0.5 rounded text-xs">
              Support : {validation.support_choisi}
            </span>
          )}
        </div>
        {!isEditing && !isRattrapageEditing && (
          <div className="flex gap-2 flex-shrink-0">
            {validation.statut === 'rattrapage' && (
              <Button 
                size="sm" 
                onClick={() => setIsRattrapageEditing(true)}
                className="text-xs bg-orange-500 hover:bg-orange-600 text-white"
              >
                <RotateCcw className="h-3 w-3 mr-1" />
                Saisir 2ème passage
              </Button>
            )}
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => setIsEditing(true)}
              className="text-xs"
            >
              {validation.statut === 'en_attente' ? 'Valider' : 'Modifier'}
            </Button>
          </div>
        )}
      </div>

      {/* Affichage de TOUS les passages */}
      {!isEditing && !isRattrapageEditing && (
        <div className="space-y-2">
          {allPassages.length === 0 ? (
            <div className="text-sm text-slate-500 italic">Aucun passage enregistré</div>
          ) : (
            allPassages.map((passage, idx) => (
              <div 
                key={idx} 
                className={`p-3 rounded-lg border ${
                  passage.statut === 'rattrapage' 
                    ? 'bg-orange-50 border-orange-200' 
                    : passage.statut === 'valide' || passage.statut === 'rattrapage_valide'
                    ? 'bg-green-50 border-green-200'
                    : 'bg-slate-50 border-slate-200'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      passage.statut === 'rattrapage' 
                        ? 'bg-orange-500 text-white' 
                        : passage.statut === 'valide' || passage.statut === 'rattrapage_valide'
                        ? 'bg-green-500 text-white'
                        : 'bg-slate-400 text-white'
                    }`}>
                      {passage.numero}
                    </span>
                    <span className="font-medium text-sm text-[#0F172A]">
                      {passage.numero === 1 ? '1er passage' : `${passage.numero}ème passage`}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {passage.statut === 'rattrapage' ? (
                      <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full font-medium flex items-center gap-1">
                        <span className="w-1.5 h-1.5 bg-orange-500 rounded-full"></span>
                        Échec - Rattrapage requis
                      </span>
                    ) : passage.statut === 'valide' || passage.statut === 'rattrapage_valide' ? (
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium flex items-center gap-1">
                        <CheckCircle className="h-3 w-3" />
                        Validé
                      </span>
                    ) : (
                      <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">
                        {passage.statut}
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                  <div className="flex items-center gap-2 text-slate-600">
                    <Calendar className="h-3.5 w-3.5 text-slate-400" />
                    <span className="font-medium">Date :</span>
                    <span>{passage.date ? new Date(passage.date).toLocaleDateString('fr-FR') : 'Non renseignée'}</span>
                  </div>
                  {passage.support && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <span className="font-medium">Support :</span>
                      <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">{passage.support}</span>
                    </div>
                  )}
                </div>
                
                {passage.commentaire && (
                  <div className="mt-2 p-2 bg-white/50 rounded border border-slate-200">
                    <p className="text-xs text-slate-500 font-medium mb-1">Commentaire :</p>
                    <p className="text-sm text-slate-700 italic">"{passage.commentaire}"</p>
                  </div>
                )}
              </div>
            ))
          )}
          
          {/* Message si en attente */}
          {validation.statut === 'en_attente' && (
            <div className="p-3 rounded-lg border border-dashed border-slate-300 bg-slate-50 text-center">
              <p className="text-sm text-slate-500">En attente de validation</p>
            </div>
          )}
        </div>
      )}

      {/* Formulaire d'édition principal */}
      {isEditing && (
        <div className="mt-3 space-y-3 pt-3 border-t border-slate-200">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Date de l'épreuve</Label>
              <input
                type="date"
                value={dateEpreuve}
                onChange={(e) => setDateEpreuve(e.target.value)}
                className="w-full mt-1 h-9 px-2 text-sm border border-slate-200 rounded"
              />
            </div>
            <div>
              <Label className="text-xs">Statut</Label>
              <select
                value={statut}
                onChange={(e) => setStatut(e.target.value)}
                className="w-full mt-1 h-9 px-2 text-sm border border-slate-200 rounded"
              >
                <option value="en_attente">En attente</option>
                <option value="valide">Validé</option>
                <option value="rattrapage">Rattrapage</option>
              </select>
            </div>
          </div>

          {epreuve.hasChoice && (
            <div>
              <Label className="text-xs">Support choisi</Label>
              <select
                value={supportChoisi}
                onChange={(e) => setSupportChoisi(e.target.value)}
                className="w-full mt-1 h-9 px-2 text-sm border border-slate-200 rounded"
              >
                <option value="">Sélectionner...</option>
                {SUPPORTS_CHOIX.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <Label className="text-xs">Commentaire (optionnel)</Label>
            <textarea
              value={commentaire}
              onChange={(e) => setCommentaire(e.target.value)}
              placeholder="Observations..."
              className="w-full mt-1 h-16 px-2 py-1 text-sm border border-slate-200 rounded resize-none"
            />
          </div>

          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="outline" onClick={() => setIsEditing(false)}>
              Annuler
            </Button>
            <Button size="sm" onClick={handleSave} className="bg-[#8DC63F] hover:bg-[#7db336] text-white">
              Enregistrer
            </Button>
          </div>
        </div>
      )}

      {/* Formulaire pour le 2ème passage (rattrapage) */}
      {isRattrapageEditing && (
        <div className="mt-3 space-y-3 pt-3 border-t border-orange-200 bg-orange-50/50 p-3 rounded">
          <p className="text-sm font-medium text-orange-700">Validation du 2ème passage</p>
          
          <div>
            <Label className="text-xs">Date du rattrapage</Label>
            <input
              type="date"
              value={dateSecondPassage}
              onChange={(e) => setDateSecondPassage(e.target.value)}
              className="w-full mt-1 h-9 px-2 text-sm border border-slate-200 rounded"
            />
          </div>

          <div>
            <Label className="text-xs">Commentaire (optionnel)</Label>
            <textarea
              value={commentaireRattrapage}
              onChange={(e) => setCommentaireRattrapage(e.target.value)}
              placeholder="Observations du rattrapage..."
              className="w-full mt-1 h-16 px-2 py-1 text-sm border border-slate-200 rounded resize-none"
            />
          </div>

          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="outline" onClick={() => setIsRattrapageEditing(false)}>
              Annuler
            </Button>
            <Button size="sm" onClick={handleRattrapageSave} className="bg-green-600 hover:bg-green-700 text-white">
              Valider le rattrapage
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// Composant pour les actions du certificat EPMSP
const CertificatActions = ({ stagiaireId, stagiaireNom }) => {
  const [loading, setLoading] = useState(false);
  const [certificatStatus, setCertificatStatus] = useState(null);
  const [sendToStagiaire, setSendToStagiaire] = useState(true);
  const [sendToEntreprise, setSendToEntreprise] = useState(true);

  useEffect(() => {
    loadCertificatStatus();
  }, [stagiaireId]);

  const loadCertificatStatus = async () => {
    try {
      const response = await axios.get(`/epmsp/certificat-status/${stagiaireId}`);
      setCertificatStatus(response.data);
    } catch (error) {
    }
  };

  const handleDownloadCertificat = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/epmsp/certificat/${stagiaireId}`, {
        responseType: 'blob'
      });
      
      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${stagiaireNom.replace(/\s+/g, '_')}_certificat_EPMSP.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Certificat téléchargé');
    } catch (error) {
      toast.error('Erreur lors du téléchargement du certificat');
    } finally {
      setLoading(false);
    }
  };

  const handleSendCertificat = async () => {
    if (!sendToStagiaire && !sendToEntreprise) {
      toast.error('Sélectionnez au moins un destinataire');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post('/epmsp/envoyer-certificat', {
        stagiaire_id: stagiaireId,
        send_to_stagiaire: sendToStagiaire,
        send_to_entreprise: sendToEntreprise
      });
      
      toast.success(`Certificat envoyé à : ${response.data.notifications_sent.join(', ')}`);
      loadCertificatStatus();
    } catch (error) {
      toast.error('Erreur lors de l\'envoi du certificat');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-4 pt-4 border-t-2 border-green-200">
      <div className="bg-green-50 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <FileCheck className="h-5 w-5 text-green-600" />
          <h4 className="font-semibold text-green-800">Certificat EPMSP</h4>
          {certificatStatus?.certificat_sent && (
            <span className="ml-auto text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
              Envoyé le {new Date(certificatStatus.certificat_info?.sent_at).toLocaleDateString('fr-FR')}
            </span>
          )}
        </div>
        
        <p className="text-sm text-green-700 mb-4">
          Toutes les épreuves sont validées. Vous pouvez générer et envoyer le certificat.
        </p>
        
        <div className="flex flex-wrap gap-3 items-center">
          <Button 
            onClick={handleDownloadCertificat}
            disabled={loading}
            className="bg-green-600 hover:bg-green-700 text-white"
            size="sm"
            data-testid="download-certificat-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Télécharger le certificat
          </Button>
          
          <div className="flex items-center gap-4 ml-4">
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
              <input 
                type="checkbox" 
                checked={sendToStagiaire} 
                onChange={(e) => setSendToStagiaire(e.target.checked)}
                className="rounded border-slate-300"
              />
              <User className="h-4 w-4" />
              Stagiaire
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
              <input 
                type="checkbox" 
                checked={sendToEntreprise} 
                onChange={(e) => setSendToEntreprise(e.target.checked)}
                className="rounded border-slate-300"
              />
              <Building2 className="h-4 w-4" />
              Entreprise
            </label>
          </div>
          
          <Button 
            onClick={handleSendCertificat}
            disabled={loading}
            variant="outline"
            size="sm"
            className="border-green-500 text-green-700 hover:bg-green-50"
            data-testid="send-certificat-btn"
          >
            <Send className="h-4 w-4 mr-2" />
            Envoyer
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EPMSPValidation;
